import React, { useState, useEffect, useRef } from 'react';
import { useContact } from './ContactContext';
import { useTranslateObject } from '../hooks/useTranslate';
import { useLocale } from './LocaleContext';
import './ContactModal.css';

const CONTENT = {
  title:       'Parlons de votre projet',
  subtitle:    'Décrivez-nous votre besoin. Un expert vous répond sous 24h ouvrées.',
  promises: [
    'Réponse garantie sous 24h',
    'Démonstration sur vos propres données',
    'Sans engagement, sans pression',
    'Accompagnement en français et arabe',
  ],
  contactLabel: 'Contact direct',
  formTitle:    'Vos coordonnées',
  fields: {
    name:     'Prénom & Nom *',
    company:  'Entreprise',
    email:    'Email professionnel *',
    phone:    'Téléphone',
    subject:  'Sujet de votre demande',
    message:  'Décrivez votre besoin *',
  },
  placeholders: {
    name:    'Karim El Mansouri',
    company: 'Nom de votre société',
    email:   'karim@societe.ma',
    phone:   '+212 6 00 00 00 00',
    message: 'Exemple : nous traitons plusieurs centaines de factures par mois manuellement et souhaitons automatiser ce processus...',
  },
  selectDefault: '— Sélectionnez un sujet —',
  interests: [
    'Documents.AI — Lecture automatique de documents',
    'Agents IA & Automatisation des processus',
    'Finance & Comptabilité intelligente',
    'Contact Center IA',
    'Modèles IA sur mesure',
    'Autre demande',
  ],
  submit:       'Envoyer ma demande',
  sending:      'Envoi en cours…',
  legal:        'En soumettant ce formulaire, vous acceptez que Smartly AI utilise vos coordonnées pour vous recontacter. Aucune donnée n\'est partagée avec des tiers.',
  successTitle: 'Message envoyé',
  successClose: 'Fermer',
  errors: {
    name:    'Votre nom est requis',
    email:   'Votre email est requis',
    emailInvalid: 'Adresse email invalide',
    message: 'Décrivez votre besoin',
  },
};

const INITIAL = { name: '', company: '', email: '', phone: '', interest: '', message: '' };

export default function ContactModal() {
  const { isOpen, prefill, closeContact } = useContact();
  const { localeKey } = useLocale();
  const isRtl = localeKey?.endsWith('-ar');
  const t = useTranslateObject(CONTENT);
  const [form, setForm]     = useState(INITIAL);
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setForm({ ...INITIAL, ...prefill });
      setStatus('idle');
      setErrors({});
      setServerError('');
      setTimeout(() => firstInputRef.current?.focus(), 80);
    }
  }, [isOpen, prefill]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') closeContact(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [closeContact]);

  const validate = () => {
    const e = {};
    if (!form.name.trim())  e.name  = t.errors.name;
    if (!form.email.trim()) e.email = t.errors.email;
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = t.errors.emailInvalid;
    if (!form.message.trim()) e.message = t.errors.message;
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setStatus('sending');
    setServerError('');

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/contact`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name:     form.name,
          company:  form.company,
          email:    form.email,
          phone:    form.phone,
          interest: form.interest || 'Non précisé',
          message:  form.message,
        }),
      });

      if (res.ok) {
        setStatus('success');
      } else {
        const data = await res.json().catch(() => ({}));
        setServerError(data.error || 'Une erreur est survenue. Veuillez réessayer.');
        setStatus('error');
      }
    } catch {
      setServerError('Impossible de contacter le serveur. Veuillez nous écrire directement à contact@smartly.ai');
      setStatus('error');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="cm-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) closeContact(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Formulaire de contact"
    >
      <div className={`cm-modal${isRtl ? ' cm-modal--rtl' : ''}`}>

        <button className="cm-close" onClick={closeContact} aria-label="Fermer">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        <div className="cm-left">
          <div className="cm-left__logo">
            <div className="cm-left__logo-dot" />
            Smartly AI
          </div>

          <h2>{t.title}</h2>
          <p>{t.subtitle}</p>

          <div className="cm-left__promises">
            {t.promises.map((text, i) => (
              <div key={i} className="cm-left__promise">
                <div className="cm-left__promise-check">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <span>{text}</span>
              </div>
            ))}
          </div>

          <div className="cm-left__contact-direct">
            <p>{t.contactLabel}</p>
            <a href="mailto:contact@smartly.ai">contact@smartly.ai</a>
          </div>
        </div>

        <div className="cm-right">
          {status === 'success' ? (
            <div className="cm-success">
              <div className="cm-success__icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <h3>{t.successTitle}</h3>
              <p>
                Merci <strong>{form.name}</strong>. Votre demande a bien été transmise à notre équipe.
                Nous vous répondrons sous 24h à l'adresse <strong>{form.email}</strong>.
              </p>
              <button className="cm-success__btn" onClick={closeContact}>{t.successClose}</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <h3 className="cm-form-title">{t.formTitle}</h3>

              <div className="cm-row">
                <div className={`cm-field${errors.name ? ' cm-field--error' : ''}`}>
                  <label htmlFor="cm-name">{t.fields.name}</label>
                  <input
                    ref={firstInputRef}
                    id="cm-name" name="name" type="text"
                    placeholder={t.placeholders.name}
                    value={form.name} onChange={handleChange}
                    autoComplete="name"
                  />
                  {errors.name && <span className="cm-error">{errors.name}</span>}
                </div>
                <div className="cm-field">
                  <label htmlFor="cm-company">{t.fields.company}</label>
                  <input
                    id="cm-company" name="company" type="text"
                    placeholder={t.placeholders.company}
                    value={form.company} onChange={handleChange}
                    autoComplete="organization"
                  />
                </div>
              </div>

              <div className="cm-row">
                <div className={`cm-field${errors.email ? ' cm-field--error' : ''}`}>
                  <label htmlFor="cm-email">{t.fields.email}</label>
                  <input
                    id="cm-email" name="email" type="email"
                    placeholder={t.placeholders.email}
                    value={form.email} onChange={handleChange}
                    autoComplete="email"
                  />
                  {errors.email && <span className="cm-error">{errors.email}</span>}
                </div>
                <div className="cm-field">
                  <label htmlFor="cm-phone">{t.fields.phone}</label>
                  <input
                    id="cm-phone" name="phone" type="tel"
                    placeholder={t.placeholders.phone}
                    value={form.phone} onChange={handleChange}
                    autoComplete="tel"
                    dir="ltr"
                  />
                </div>
              </div>

              <div className="cm-field">
                <label htmlFor="cm-interest">{t.fields.subject}</label>
                <div className="cm-select-wrap">
                  <select
                    id="cm-interest" name="interest" value={form.interest} onChange={handleChange}
                  >
                    <option value="">{t.selectDefault}</option>
                    {t.interests.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                  <svg
                    width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </div>
              </div>

              <div className={`cm-field${errors.message ? ' cm-field--error' : ''}`}>
                <label htmlFor="cm-message">{t.fields.message}</label>
                <textarea
                  id="cm-message" name="message" rows={4}
                  placeholder={t.placeholders.message}
                  value={form.message} onChange={handleChange}
                />
                {errors.message && <span className="cm-error">{errors.message}</span>}
              </div>

              {status === 'error' && serverError && (
                <div className="cm-send-error">
                  {serverError}
                </div>
              )}

              <button type="submit" className="cm-submit" disabled={status === 'sending'}>
                {status === 'sending' ? (
                  <><span className="cm-spinner" /> {t.sending}</>
                ) : (
                  <>
                    {t.submit}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13"/>
                      <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                    </svg>
                  </>
                )}
              </button>

              <p className="cm-legal">{t.legal}</p>
            </form>
          )}
        </div>

      </div>
    </div>
  );
}
